import React, { useState, useEffect, Fragment } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  Typography,
  TextField,
  Autocomplete,
  CircularProgress,
  Stack,
  useMediaQuery,
  useTheme,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import useFetchObjects from "../../../../api/useFetchObjects";
import useUpdate from "../../../../api/useUpdate";

const EditMedicinePaymentListDialog = ({ open, onClose, payment, token }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [formData, setFormData] = useState({
    medicine_company: null,
    amount: "",
    date: "",
    invoice_number: "",
    images: [], // New uploaded files
  });
  const [formErrors, setFormErrors] = useState({});
  const [companyData, setCompanyData] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);

  // Existing images from API with {id, image, ...}
  const [existingImages, setExistingImages] = useState([]);
  // IDs of images removed by user to send to backend
  const [removedImageIds, setRemovedImageIds] = useState([]);

  const { data: companies, loading: loadingCompanies } = useFetchObjects(
    ["medicine-companies"],
    "medicine-companies/",
    token
  );

  const { handleUpdate, loading: saving } = useUpdate(
    "purchase-payments",
    token
  );

  // Populate form and companies data when payment or companies change
  useEffect(() => {
    if (companies) {
      setCompanyData(
        companies.map((company) => ({
          id: company.id,
          label: company.name,
          total_paid: company.total_paid,
          remaining_balance: company.remaining_balance,
        }))
      );
    }
  }, [companies]);

  // Load payment data into form and images on open/payment change
  useEffect(() => {
    if (payment && companies) {
      const selected = companies.find((c) => c.id === payment.medicine_company);
      setSelectedCompany(
        selected
          ? {
              id: selected.id,
              label: selected.name,
              total_paid: selected.total_paid,
              remaining_balance: selected.remaining_balance,
            }
          : null
      );

      setFormData({
        medicine_company: payment.medicine_company,
        amount: payment.amount || "",
        date: payment.date || "",
        invoice_number: payment.invoice_number || "",
        images: [], // new uploads empty initially
      });
      setFormErrors({});
      setRemovedImageIds([]);
      setExistingImages(payment.images || []);
    } else {
      // Reset if no payment
      setFormData({
        medicine_company: null,
        amount: "",
        date: "",
        invoice_number: "",
        images: [],
      });
      setSelectedCompany(null);
      setExistingImages([]);
      setRemovedImageIds([]);
      setFormErrors({});
    }
  }, [payment, companies]);

  const validateForm = () => {
    const errors = {};
    if (!formData.medicine_company) {
      errors.medicine_company = t("finance.companyRequired");
    }
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      errors.amount = t("finance.amountRequired");
    } else if (
      selectedCompany &&
      parseFloat(formData.amount) >
        parseFloat(selectedCompany.remaining_balance)
    ) {
      errors.amount = t("finance.amountExceedsBalanceDue");
    }
    if (!formData.date) {
      errors.date = t("finance.dateRequired");
    }
    // invoice_number optional validation can be added here

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    const data = new FormData();
    data.append("medicine_company", formData.medicine_company);
    data.append("amount", formData.amount);
    data.append("date", formData.date);
    data.append("invoice_number", formData.invoice_number);

    // Append new files
    formData.images.forEach((file) => {
      data.append("images", file);
    });

    // Append IDs of removed existing images to be deleted on backend
    removedImageIds.forEach((id) => {
      data.append("remove_images", id);
    });

    handleUpdate(payment?.id, data);
    onClose();
  };

  const handleCompanySelect = (value) => {
    setFormData({ ...formData, medicine_company: value?.id || null });
    setSelectedCompany(value);
    setFormErrors({ ...formErrors, medicine_company: "" });
  };

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);
    setFormData((prev) => ({
      ...prev,
      images: [...prev.images, ...newFiles],
    }));
  };

  const handleRemoveFile = (index) => {
    setFormData((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
  };

  const handleRemoveExistingImage = (id) => {
    setExistingImages((imgs) => imgs.filter((img) => img.id !== id));
    setRemovedImageIds((ids) => [...ids, id]);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle
        sx={{
          fontWeight: "bold",
          backgroundColor: "primary.main",
          color: "white",
          textAlign: "center",
          py: 2,
          position: "relative",
        }}
      >
        {t("finance.editMedicinePayment")}
        <IconButton
          aria-label={t("finance.close")}
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "white",
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          background: "linear-gradient(to bottom, #f9fafa, #ffffff)",
          p: 4,
        }}
      >
        <form onSubmit={handleSave}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={8}>
              <Card
                sx={{
                  backgroundColor: "#ffffff",
                  borderRadius: 3,
                  boxShadow: 3,
                  p: 3,
                }}
              >
                <Typography
                  variant="h5"
                  fontWeight={600}
                  color="primary"
                  mb={2}
                  sx={{ fontSize: isSmallScreen ? "1.2rem" : "1.5rem" }}
                >
                  {t("finance.paymentForm")}
                </Typography>
                <Divider sx={{ mb: 2 }} />

                {loadingCompanies ? (
                  <Box textAlign="center" my={4}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <Autocomplete
                        options={companyData.length ? companyData : []}
                        value={selectedCompany}
                        getOptionLabel={(option) => option.label || ""}
                        onChange={(e, value) => handleCompanySelect(value)}
                        isOptionEqualToValue={(option, value) =>
                          option.id === value.id
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={t("finance.selectCompany")}
                            variant="outlined"
                            error={!!formErrors.medicine_company}
                            helperText={formErrors.medicine_company}
                            fullWidth
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        label={t("finance.amountPaid")}
                        variant="outlined"
                        fullWidth
                        type="number"
                        value={formData.amount}
                        onChange={(e) =>
                          setFormData({ ...formData, amount: e.target.value })
                        }
                        error={!!formErrors.amount}
                        helperText={formErrors.amount}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        label={t("finance.paymentDate")}
                        variant="outlined"
                        fullWidth
                        type="date"
                        value={formData.date}
                        onChange={(e) =>
                          setFormData({ ...formData, date: e.target.value })
                        }
                        InputLabelProps={{ shrink: true }}
                        error={!!formErrors.date}
                        helperText={formErrors.date}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        label={t("finance.invoiceNumber", "Invoice Number")}
                        variant="outlined"
                        fullWidth
                        value={formData.invoice_number}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            invoice_number: e.target.value,
                          })
                        }
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Button
                        variant="outlined"
                        component="label"
                        fullWidth
                        sx={{
                          borderStyle: "dashed",
                          py: 2,
                          borderRadius: 1,
                          textTransform: "none",
                        }}
                      >
                        {t("finance.uploadBills", "Upload Bills")}
                        <input
                          type="file"
                          multiple
                          hidden
                          onChange={handleFileChange}
                          accept="image/*"
                        />
                      </Button>
                    </Grid>

                    {/* Existing Images */}
                    {existingImages.length > 0 && (
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" gutterBottom>
                          {t("finance.existingBills","Exsting Bills")}
                        </Typography>
                        <Stack direction="row" spacing={2} flexWrap="wrap">
                          {existingImages.map((img) => (
                            <Box
                              key={img.id}
                              sx={{
                                border: "1px solid",
                                borderColor: theme.palette.divider,
                                borderRadius: 1,
                                p: 1,
                                position: "relative",
                                width: 100,
                                height: 100,
                                overflow: "hidden",
                              }}
                            >
                              <img
                                src={img.image}
                                alt="existing preview"
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                }}
                              />
                              <IconButton
                                size="small"
                                onClick={() =>
                                  handleRemoveExistingImage(img.id)
                                }
                                sx={{
                                  position: "absolute",
                                  top: 2,
                                  right: 2,
                                  bgcolor: "rgba(255,255,255,0.7)",
                                }}
                              >
                                &times;
                              </IconButton>
                            </Box>
                          ))}
                        </Stack>
                      </Grid>
                    )}

                    {/* New Uploaded Images */}
                    {formData.images.length > 0 && (
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" gutterBottom>
                          {t("finance.newUploadedBills", "New Uploaded Bills")}
                        </Typography>
                        <Stack direction="row" spacing={2} flexWrap="wrap">
                          {formData.images.map((file, idx) => (
                            <Box
                              key={idx}
                              sx={{
                                border: "1px solid",
                                borderColor: theme.palette.divider,
                                borderRadius: 1,
                                p: 1,
                                position: "relative",
                                width: 100,
                                height: 100,
                                overflow: "hidden",
                              }}
                            >
                              <img
                                src={URL.createObjectURL(file)}
                                alt="new preview"
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                }}
                              />
                              <IconButton
                                size="small"
                                onClick={() => handleRemoveFile(idx)}
                                sx={{
                                  position: "absolute",
                                  top: 2,
                                  right: 2,
                                  bgcolor: "rgba(255,255,255,0.7)",
                                }}
                              >
                                &times;
                              </IconButton>
                            </Box>
                          ))}
                        </Stack>
                      </Grid>
                    )}
                  </Grid>
                )}
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  backgroundColor: "#f5f5f5",
                  borderRadius: 3,
                  boxShadow: 3,
                  overflow: "hidden",
                }}
              >
                <CardContent>
                  <Typography
                    variant="h5"
                    fontWeight={600}
                    color="primary"
                    mb={2}
                    sx={{ fontSize: isSmallScreen ? "1.2rem" : "1.5rem" }}
                  >
                    {t("finance.companyDetails")}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  {selectedCompany ? (
                    <Stack spacing={3}>
                      <Box>
                        <Typography variant="body2" color="textSecondary">
                          {t("finance.companyName")}
                        </Typography>
                        <Typography variant="h6" fontWeight={700}>
                          {selectedCompany.label}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="textSecondary">
                          {t("finance.totalPaid")}
                        </Typography>
                        <Typography variant="h6">
                          {selectedCompany.total_paid} ؋
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="textSecondary">
                          {t("finance.remainingBalance")}
                        </Typography>
                        <Typography variant="h6" color="error">
                          {selectedCompany.remaining_balance} ؋
                        </Typography>
                      </Box>
                    </Stack>
                  ) : (
                    <Typography textAlign="center" color="textSecondary">
                      {t("finance.noCompanySelected")}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </form>
      </DialogContent>

      <DialogActions
        sx={{
          px: 4,
          py: 3,
          backgroundColor: "#f9fafa",
          justifyContent: "space-between",
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          color="secondary"
          sx={{
            borderRadius: 2,
            px: 3,
            fontWeight: "bold",
            textTransform: "none",
          }}
          disabled={saving}
        >
          {t("finance.cancel")}
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          sx={{
            borderRadius: 2,
            px: 3,
            fontWeight: "bold",
            textTransform: "none",
          }}
          disabled={saving}
        >
          {saving ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t("finance.save")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditMedicinePaymentListDialog;
